"use client";

import { useCallback, useEffect, useId, useState } from "react";
import { useRouter } from "next/navigation";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { EmailGateModal } from "@/components/leadgate/email-gate-modal";
import { ValuationBadge } from "@/components/valuation-badge";
import { useDebouncedSearch } from "@/lib/use-debounced-search";
import { cn } from "@/lib/utils";
import type { ScoreSummary } from "@/lib/data/score-summary";

const GATE_STORAGE_KEY = "ek_searched_symbols";
const GATE_FREE_SEARCHES = 3;

function hasVerifiedCookie(): boolean {
  return document.cookie.split("; ").includes("ek_verified=1");
}

export function SearchHero({
  variant = "hero",
  currentSymbol,
}: {
  /** "split" (Landingpage-Feedback 2026-08-17, Punkt 8): gleiche große Schriftgröße/
   * Icon-Größe wie "hero", aber ohne eigenen Rahmen/Schatten/Rundung - für den Einsatz
   * als linke Hälfte der neuen zusammengesetzten Such-/Gratis-Aktien-Leiste auf der
   * Startseite, die diese Chrome-Elemente selbst als gemeinsamer Rahmen um beide
   * Hälften traegt (siehe app/page.tsx). */
  variant?: "hero" | "compact" | "split";
  /** Wenn gesetzt (auf /[symbol]-Seiten), zaehlt dieses Symbol fuers Lead-Gate mit. */
  currentSymbol?: string;
}) {
  const router = useRouter();
  // Bewusst lokaler State statt nuqs' useQueryState/`?q=`-URL-Sync (Bug-Fix, Erik
  // 2026-08-19: "egal welche Aktie ich eingebe, es wird mir nicht vorgeschlagen...
  // kann nichts anklicken") - nichts im Code liest den `q`-Parameter jemals wieder aus
  // (kein Deep-Link/Vorbefuellung), er diente nur diesem Eingabefeld selbst. Per
  // Playwright-Diagnose bestaetigt: jeder Tastendruck loeste trotzdem eine ECHTE
  // Navigation aus (Playwright meldete `framenavigated` fuer jedes `?q=t`, `?q=te`, ...),
  // nicht nur eine flache URL-Aenderung - auf einer Route wie `/[symbol]`, die selbst
  // `searchParams` liest, reicht das, damit Next.js' App Router den Server-Baum bei
  // JEDEM Tastendruck neu anfordert und dabei den lokalen `isOpen`-Zustand dieser
  // Komponente zurücksetzt, noch bevor die (korrekt gefetchten) Vorschlaege ueberhaupt
  // angezeigt werden koennen. Ein einfacher `useState` braucht keinen Router-Umweg.
  const [query, setQuery] = useState("");
  const suggestions = useDebouncedSearch(query);
  // Bewertungs-Ampel im Such-Dropdown (backlog.md -> Punkt 20, offener Rest) - bewusst
  // NICHT Teil der /api/search-Antwort (die muss schnell bleiben, siehe Backlog-
  // Begruendung "wuerde die Autocomplete-Suche verlangsamen"), sondern asynchron
  // nachgeladen ueber die bereits bestehende /api/recent-scores-Route (dasselbe
  // gecachte loadScoreSummary() wie Screener/Community-Trends, kein neuer Endpunkt).
  // Faengt erst NACH dem Anzeigen der Vorschlaege an - blockiert das Dropdown nie.
  const [valuations, setValuations] = useState<Map<string, ScoreSummary>>(new Map());
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isOpen, setIsOpen] = useState(false);
  const [gateBlocked, setGateBlocked] = useState(false);
  const [gateCheckedSymbol, setGateCheckedSymbol] = useState<string | undefined>(undefined);

  // Bewusst als Render-Phase-Update statt useEffect (offiziell von React fuer
  // "State an eine geaenderte Prop anpassen" vorgesehen, siehe react.dev/learn/
  // you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes). Die
  // Bedingung currentSymbol !== gateCheckedSymbol verhindert eine Endlosschleife,
  // da setGateCheckedSymbol im selben Aufruf den Zustand sofort angleicht.
  if (typeof window !== "undefined" && currentSymbol && currentSymbol !== gateCheckedSymbol) {
    setGateCheckedSymbol(currentSymbol);
    if (!hasVerifiedCookie()) {
      let symbols: string[] = [];
      try {
        symbols = JSON.parse(localStorage.getItem(GATE_STORAGE_KEY) ?? "[]");
      } catch {
        symbols = [];
      }
      if (!symbols.includes(currentSymbol)) {
        symbols = [...symbols, currentSymbol];
        localStorage.setItem(GATE_STORAGE_KEY, JSON.stringify(symbols));
      }
      if (symbols.length > GATE_FREE_SEARCHES) {
        setGateBlocked(true);
      }
    }
  }

  // activeIndex zuruecksetzen, sobald sich die Vorschlagsliste aendert (vorher inline nach
  // dem setSuggestions() im Fetch-Handler - useDebouncedSearch kapselt den Fetch jetzt,
  // daher hier als eigener Effect statt im Hook selbst, der activeIndex nicht kennt).
  useEffect(() => {
    // setState nicht synchron im Effect-Body (react-hooks/set-state-in-effect).
    const id = setTimeout(() => setActiveIndex(-1), 0);
    return () => clearTimeout(id);
  }, [suggestions]);

  useEffect(() => {
    const missing = suggestions.map((s) => s.symbol).filter((symbol) => !valuations.has(symbol));
    if (missing.length === 0) return;

    const controller = new AbortController();
    fetch("/api/recent-scores", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ symbols: missing.slice(0, 10) }),
      signal: controller.signal,
    })
      .then((res) => (res.ok ? (res.json() as Promise<ScoreSummary[]>) : []))
      .then((results) => {
        setValuations((prev) => {
          const next = new Map(prev);
          for (const result of results) next.set(result.symbol, result);
          return next;
        });
      })
      .catch(() => {
        // Bewertungs-Ampel ist rein informativ - kein Fehlerzustand fuers Dropdown noetig.
      });

    return () => controller.abort();
  }, [suggestions, valuations]);

  const navigateToSymbol = useCallback(
    (symbol: string) => {
      setIsOpen(false);
      router.push(`/${encodeURIComponent(symbol)}`);
    },
    [router],
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || suggestions.length === 0) {
      if (event.key === "Enter" && query.trim().length > 0) {
        navigateToSymbol(query.trim());
      }
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const chosen = suggestions[activeIndex] ?? suggestions[0];
      navigateToSymbol(chosen.symbol);
    } else if (event.key === "Escape") {
      setIsOpen(false);
    }
  };

  const showDropdown = isOpen && query.trim().length >= 2 && suggestions.length > 0;

  // Eindeutige IDs statt der frueheren hartkodierten Strings (Bug-Fix Erik 2026-08-19,
  // volle Herleitung in app/[symbol]/layout.tsx) - SearchHero rendert an mehreren
  // Stellen der App (Header UND Startseiten-Hero), ein hartkodiertes id="site-search-
  // input" kollidierte, sobald zwei Instanzen gleichzeitig im DOM standen (Doppel-Header
  // waehrend Next.js' Loading->Page-Uebergang war die Ursache, aber `useId()` macht diese
  // Klasse von Bug strukturell unmoeglich statt nur den einen bekannten Ausloeser zu beheben).
  const inputId = useId();
  const listboxId = useId();
  const isCompact = variant === "compact";
  const isSplit = variant === "split";

  return (
    <div className={cn(isSplit ? "h-full w-full" : "w-full", !isSplit && (isCompact ? "max-w-sm" : "max-w-2xl"))}>
      <div className="relative h-full">
        <Search
          className={cn(
            "pointer-events-none absolute top-1/2 -translate-y-1/2 text-muted-foreground",
            isCompact ? "left-3 h-3.5 w-3.5" : "left-5 h-5 w-5",
          )}
          aria-hidden="true"
        />
        <Input
          id={inputId}
          data-search-input="true"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 120)}
          onKeyDown={handleKeyDown}
          placeholder="Name, ISIN oder WKN …"
          aria-label="Aktie suchen: Name, ISIN oder WKN"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls={listboxId}
          aria-autocomplete="list"
          className={cn(
            isSplit
              ? "h-full rounded-none border-0 bg-transparent pl-14 pr-4 text-lg shadow-none focus-visible:ring-0"
              : "border-border bg-card shadow-sm focus-visible:ring-gold-text",
            isCompact ? "h-9 rounded-full pl-8 pr-3 text-sm" : !isSplit && "h-16 rounded-2xl pl-14 pr-5 text-lg",
          )}
        />
        {showDropdown && (
          <ul
            id={listboxId}
            role="listbox"
            className="absolute z-20 mt-2 w-full overflow-hidden rounded-2xl border border-border bg-card shadow-md"
          >
            {suggestions.map((s, i) => (
              <li
                key={`${s.symbol}-${s.isin ?? i}`}
                role="option"
                aria-selected={i === activeIndex}
                onMouseDown={() => navigateToSymbol(s.symbol)}
                onMouseEnter={() => router.prefetch(`/${encodeURIComponent(s.symbol)}`)}
                className={cn(
                  "flex cursor-pointer items-center justify-between gap-2 px-4 py-2.5 text-sm",
                  i === activeIndex ? "bg-muted" : "hover:bg-muted",
                )}
              >
                <span>
                  <span className="font-medium text-ink">{s.name}</span>
                  <span className="ml-2 text-muted-foreground">{s.symbol}</span>
                </span>
                <ValuationBadge
                  pe={valuations.get(s.symbol)?.pe}
                  sectorMedian={valuations.get(s.symbol)?.peSector}
                  ownMedian={valuations.get(s.symbol)?.ownPeMedian}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      {gateBlocked && <EmailGateModal />}
    </div>
  );
}
